<template>
  <div class="fleet-logs">
    <header class="fleet-logs__head">
      <div>
        <h1>Logs</h1>
        <p>
          Every subscribed log source across the fleet, newest first. Filtering
          by file path lives on a server's own Logs tab — a path means something
          different on each box.
        </p>
      </div>
    </header>

    <p v-if="loadError" class="fleet-logs__error">{{ loadError }}</p>

    <div class="fleet-logs__toolbar">
      <input
        v-model="filters.q"
        class="fleet-logs__search"
        type="search"
        placeholder="Search log lines…"
        @keyup.enter="reload"
      />
      <select v-model="filters.serverId" class="fleet-logs__select" @change="reload">
        <option value="">All servers</option>
        <option v-for="server in servers" :key="server.id" :value="String(server.id)">
          {{ server.label }}
        </option>
      </select>
      <select v-model="filters.domain" class="fleet-logs__select" @change="reload">
        <option value="">All domains</option>
        <option v-for="row in domains" :key="row.domain" :value="row.domain">
          {{ row.domain }}{{ row.servers > 1 ? ` (${row.servers})` : '' }}
        </option>
      </select>
      <select v-model="filters.categoryGroup" class="fleet-logs__select" @change="reload">
        <option value="">All source types</option>
        <option value="web-access">Web access</option>
        <option value="web-error">Web error</option>
        <option value="system">System</option>
        <option value="db">Database</option>
        <option value="security">Security</option>
      </select>
      <label class="fleet-logs__toggle">
        <input v-model="filters.errorsOnly" type="checkbox" @change="reload" />
        Errors &amp; worse only
      </label>
      <button type="button" class="fleet-logs__btn" @click="reload">Refresh</button>
    </div>

    <div class="fleet-logs__panel">
      <p v-if="loading && !entries.length" class="fleet-logs__empty">Loading…</p>
      <p v-else-if="!entries.length" class="fleet-logs__empty">
        No log lines match. A source only produces lines once an agent is
        subscribed to it — subscribe from a server's own Logs tab.
      </p>

      <div
        v-for="entry in entries"
        :key="entry.id"
        class="fleet-logs__line"
        :class="`is-${severityTone(entry.severity)}`"
      >
        <span class="fleet-logs__ts">{{ formatTime(entry.line_ts || entry.received_at) }}</span>
        <router-link
          class="fleet-logs__server"
          :to="`/servers/${entry.server_id}`"
          :title="entry.log_path"
        >{{ entry.server || `#${entry.server_id}` }}</router-link>
        <span v-if="entry.domain" class="fleet-logs__domain">{{ entry.domain }}</span>
        <span class="fleet-logs__sev" :class="`is-${severityTone(entry.severity)}`">
          {{ entry.severity }}
        </span>
        <span class="fleet-logs__text">{{ truncate(entry.line) }}</span>
      </div>

      <button
        v-if="hasMore"
        type="button"
        class="fleet-logs__more"
        @click="loadMore"
      >Load more</button>
    </div>
  </div>
</template>

<script setup>
/**
 * Fleet-wide Logs — mounted at `/logs` by
 * `app\module\observability\Controller\FleetDisplayController::logs()`.
 *
 * The estate-scale twin of the per-server Logs tab, promoted by the
 * 2026-08-01 UI review §02. Two conscious reductions against that tab:
 *
 *  - **No file-path rail.** `/var/log/apache2/error.log` exists on every
 *    server; a fleet filter on it would silently mean something different per
 *    row. Server + domain + source-type is the fleet vocabulary.
 *  - **No "Refresh sources".** Requesting a `logs.list` catalogue refresh is a
 *    per-agent command; there is no unambiguous agent to ask from here.
 *
 * Rendering is deliberately plain rows with a hard line cap — the same lesson
 * as commit `b59a09a` ("fix Logs tab renderer freeze on large log batches"),
 * where an unbounded, richly-decorated render of a 200-entry batch froze the
 * Chrome renderer for ~30 seconds.
 *
 * Query params (`?domain=&server_id=`) are honoured on mount so the Fleet
 * pressure view's "Logs →" link on a busiest-vhost row lands pre-filtered.
 */
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';

const props = defineProps({
  ...commonProps,
  apiBase: { type: String, default: '/api/v1/observability/fleet' },
});

const PAGE_SIZE = 100;

/** Same 2000-char cap the per-server tab settled on — a single 4MB stack trace must not lock the tab. */
const MAX_LINE_CHARS = 2000;

const POLL_MS = 30000;

const api = useApi();
const route = useRoute();

const entries = ref([]);
const servers = ref([]);
const domains = ref([]);
const hasMore = ref(false);
const loading = ref(true);
const loadError = ref(null);
let pollHandle = null;

const filters = reactive({
  q: '',
  serverId: '',
  domain: '',
  categoryGroup: '',
  errorsOnly: false,
});

function params(offset) {
  const out = { limit: PAGE_SIZE, offset };
  if (filters.q) out.q = filters.q;
  if (filters.serverId) out.server_id = filters.serverId;
  if (filters.domain) out.domain = filters.domain;
  if (filters.categoryGroup) out.category_group = filters.categoryGroup;
  if (filters.errorsOnly) out.severity_min = 'error';

  return out;
}

async function load({ append = false } = {}) {
  try {
    const data = await api.get(`${props.apiBase}/logs`, {
      params: params(append ? entries.value.length : 0),
    });
    entries.value = append ? [...entries.value, ...(data.entries || [])] : (data.entries || []);
    servers.value = data.servers || [];
    domains.value = data.domains || [];
    hasMore.value = Boolean(data.has_more);
    loadError.value = null;
  } catch {
    loadError.value = 'Could not load fleet logs — check your connection and try again.';
  } finally {
    loading.value = false;
  }
}

function reload() {
  loading.value = true;
  load({ append: false });
}

function loadMore() {
  load({ append: true });
}

onMounted(() => {
  // Deep-link support: `/logs?domain=hrindependents.co.uk&server_id=3`.
  if (route.query.domain) filters.domain = String(route.query.domain);
  if (route.query.server_id) filters.serverId = String(route.query.server_id);
  if (route.query.q) filters.q = String(route.query.q);

  load();
  // Poll only the first page — appending pages then silently replacing them
  // under a reading operator would be worse than slightly stale.
  pollHandle = setInterval(() => {
    if (entries.value.length <= PAGE_SIZE) {
      load({ append: false });
    }
  }, POLL_MS);
});

onBeforeUnmount(() => {
  if (pollHandle) {
    clearInterval(pollHandle);
  }
});

function severityTone(severity) {
  if (severity === 'critical' || severity === 'error') return 'bad';
  if (severity === 'warning') return 'warn';
  return 'info';
}

function truncate(line) {
  const text = String(line ?? '');
  return text.length > MAX_LINE_CHARS ? `${text.slice(0, MAX_LINE_CHARS)}…` : text;
}

function formatTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleString(undefined, {
    month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}
</script>

<style lang="scss">
.fleet-logs {
  &__head {
    margin-bottom: 16px;

    h1 { font-size: 20px; font-weight: 600; margin: 0 0 4px; }
    p { font-size: 13px; color: var(--theme-text-secondary); margin: 0; max-width: 74ch; }
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12.5px;
    margin-bottom: 14px;
  }

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 12px;
  }

  &__search, &__select {
    font-size: 12.5px;
    padding: 7px 10px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-primary);
  }

  &__search { flex: 1 1 240px; min-width: 180px; }

  &__toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--theme-text-secondary);
  }

  &__btn {
    font-size: 12.5px;
    font-weight: 600;
    padding: 7px 14px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    cursor: pointer;
  }

  &__panel {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__empty {
    padding: 16px 14px;
    margin: 0;
    font-size: 12.5px;
    color: var(--theme-text-tertiary);
  }

  &__line {
    display: flex;
    gap: 10px;
    align-items: baseline;
    padding: 5px 14px;
    border-bottom: 1px solid var(--theme-border-soft);
    font-family: var(--theme-font-mono);
    font-size: 11.5px;

    &:last-of-type { border-bottom: 0; }
    &.is-bad  { background: rgba(209, 67, 67, 0.05); }
    &.is-warn { background: rgba(224, 160, 48, 0.05); }
  }

  &__ts {
    flex: 0 0 150px;
    color: var(--theme-text-tertiary);
    white-space: nowrap;
  }

  &__server {
    flex: 0 0 130px;
    font-weight: 600;
    color: var(--theme-accent-dark);
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__domain {
    flex: 0 0 150px;
    color: var(--theme-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sev {
    flex: 0 0 62px;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 700;

    &.is-bad  { color: #a53232; }
    &.is-warn { color: #8a5a0d; }
    &.is-info { color: var(--theme-text-tertiary); }
  }

  &__text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--theme-text-primary);
  }

  &__more {
    display: block;
    width: 100%;
    padding: 10px;
    border: 0;
    border-top: 1px solid var(--theme-border);
    background: var(--theme-bg-content);
    font-size: 12px;
    font-weight: 600;
    color: var(--theme-accent-dark);
    cursor: pointer;
  }
}
</style>
